import React, {useState} from 'react';
import {Alert, Box, Collapse, Stack, TextField} from '@mui/material';
import {
    ApprovalInstance,
    ApprovalStepInstance,
    ApproverVote,
    ProcessActionRequest,
    processAction, CancelRequest,
} from '../../../api/ApprovalApi';
import {Button} from "../../../components/Button";
import PopoverMenu, {MenuItemType} from "../../../components/PopoverMenu";
import {cancelMyApproval} from "../../../api/MeApi";
import {useTranslation} from "react-i18next";

interface VoteConfig {
    label: string;
    color: 'primary' | 'secondary' | 'attention';
    /**
     * When true a text input is shown before the action is submitted.
     * For REJECTED this field is treated as required; for others optional.
     */
    needsInput: boolean;
    inputLabel?: string;
    inputRequired?: boolean;
}

const VOTE_CONFIG: Record<ApproverVote, VoteConfig> = {
    APPROVED: {
        label: 'Genehmigen',
        color: 'primary',
        needsInput: false
    },
    REJECTED: {
        label: 'Ablehnen',
        color: 'attention',
        needsInput: true,
        inputLabel: 'Ablehnungsgrund',
        inputRequired: true,
    },
    RETURNED: {
        label: 'Zurückgeben',
        color: 'primary',
        needsInput: true,
        inputLabel: 'Kommentar für Antragsteller',
        inputRequired: false,
    },
    ABSTAINED: {
        label: 'Enthalten',
        color: 'primary',
        needsInput: false
    },
    COMMENTED: {
        label: 'Kommentieren',
        color: 'secondary',
        needsInput: true,
        inputLabel: 'Kommentar',
        inputRequired: true,
    },
};

const CANCEL_INPUT_LABEL = 'Grund für Stornierung';

type ActionPhase =
    | { type: 'idle' }
    | { type: 'composingVote'; vote: ApproverVote; text: string }
    | { type: 'composingCancel'; text: string }
    | { type: 'submitting' }
    | { type: 'error'; message: string; recoverable: boolean };

const toUserMessage = (error: unknown): string => {
    if (error instanceof Error) {
        const msg = error.message.toUpperCase();
        if (msg.includes('FOUR_EYES') || msg.includes('REQUESTER')) {
            return 'Sie können keinen eigenen Antrag genehmigen (Vier-Augen-Prinzip).';
        }
        if (msg.includes('DUPLICATE') || msg.includes('ALREADY_VOTED')) {
            return 'Sie haben auf diesem Schritt bereits abgestimmt.';
        }
        if (msg.includes('STEP_NOT_ACTIVE')) {
            return 'Dieser Schritt ist nicht mehr aktiv. Bitte laden Sie die Seite neu.';
        }
        if (msg.includes('ALREADY_TERMINAL')) {
            return 'Der Vorgang wurde bereits abgeschlossen.';
        }
    }
    return 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
};

export interface ApprovalActionsProps<T extends ApprovalInstance> {
    instance: T;
    onSuccess: (updated: T) => void;
    onShowDetails?: (instance: T) => void;
    showCompact?: boolean;
}

const ApprovalActions = <T extends ApprovalInstance>({
                                                         instance,
                                                         onSuccess,
                                                         showCompact = true,
                                                         onShowDetails
                                                     }: ApprovalActionsProps<T>) => {

    const {t} = useTranslation();

    const [phase, setPhase] = useState<ActionPhase>({type: 'idle'});

    const {eligibleVotes, canCancel} = instance;

    // Nothing to render for this user on this instance
    if (eligibleVotes.length === 0 && !canCancel) return null;

    // The active step is needed only to obtain the stepInstanceId for the API call.
    // Eligibility is already determined server-side; no further checks are needed here.
    const activeStep: ApprovalStepInstance | undefined =
        instance.stepInstances.find(s => s.status === 'WAITING_APPROVAL');

    const handleVoteClick = (vote: ApproverVote) => {
        const config = VOTE_CONFIG[vote];
        if (config.needsInput) {
            setPhase({type: 'composingVote', vote, text: ''});
        } else {
            submitVote(vote, null, null);
        }
    };

    const handleCancelClick = () => {
        setPhase({type: 'composingCancel', text: ''});
    };

    const handleComposedVoteSubmit = () => {
        if (phase.type !== 'composingVote') return;

        const config = VOTE_CONFIG[phase.vote];
        // requireRejectionComment comes from the step definition snapshot on the step instance
        const requireRejectionComment =
            activeStep?.requireRejectionComment ?? (phase.vote === 'REJECTED');
        const effectiveRequired = config.inputRequired || requireRejectionComment;

        if (effectiveRequired && !phase.text.trim()) return;

        const isRejection = phase.vote === 'REJECTED';
        submitVote(
            phase.vote,
            isRejection ? null : phase.text.trim() || null,
            isRejection ? phase.text.trim() || null : null,
        );
    };

    const handleComposedCancelSubmit = () => {
        if (phase.type !== 'composingCancel') return;
        submitCancel(phase.text.trim() || '');
    };

    const submitVote = async (
        vote: ApproverVote,
        comment: string | null,
        rejectionReason: string | null,
    ) => {
        if (!activeStep) return;

        setPhase({type: 'submitting'});

        const request: ProcessActionRequest = {
            vote,
            comment,
            rejectionReason,
            idempotencyKey: crypto.randomUUID(),
            adminOverride: null,
        };

        try {
            const response = await processAction<T>(instance.id, activeStep.id, request);
            if (response.success && response.data) {
                onSuccess(response.data);

            } else {
                setPhase({
                    type: 'error',
                    message: response.error?.message ?? 'Unbekannter Fehler.',
                    recoverable: true,
                });
            }
        } catch (e) {
            setPhase({type: 'error', message: toUserMessage(e), recoverable: true});
        }
    };

    const submitCancel = async (reason: string) => {
        setPhase({type: 'submitting'});

        const request: CancelRequest = {reason};

        try {
            // TODO: Refactor cancelMyApproval to use through useMe() hook
            const response = await cancelMyApproval<T>(instance.id, request);
            if (response.success && response.data) {
                onSuccess(response.data);
            } else {
                setPhase({
                    type: 'error',
                    message: response.error?.message ?? 'Stornierung fehlgeschlagen.',
                    recoverable: true,
                });
            }
        } catch (e) {
            setPhase({type: 'error', message: toUserMessage(e), recoverable: true});
        }
    };

    const renderIdleButtons = () => {

        const secondaryVotes = eligibleVotes.filter(v => v !== 'APPROVED');

        const menuItems: MenuItemType[] = [
            ...secondaryVotes.map(vote => ({
                id: vote,
                label: VOTE_CONFIG[vote].label,
                onClick: () => handleVoteClick(vote)
            })),
            ...(canCancel ? [{
                id: 'cancel',
                label: t('approvals.actions.cancel'),
                onClick: handleCancelClick
            }] : []),
        ];

        return (
            <Stack direction="row" className={'items-center'} spacing={1} flexWrap="wrap" useFlexGap>

                {onShowDetails && (
                    <Button
                        variant={'outlined'}
                        size={'small'}
                        onClick={() => onShowDetails?.(instance)}
                    >
                        {t('approvals.actions.details')}
                    </Button>
                )}

                {eligibleVotes.includes('APPROVED') && (
                    <Button
                        variant={'contained'}
                        color={VOTE_CONFIG['APPROVED'].color}
                        onPress={() => handleVoteClick('APPROVED')}
                    >
                        {VOTE_CONFIG['APPROVED'].label}
                    </Button>
                )}

                {showCompact && menuItems.length > 1 && (
                    <PopoverMenu
                        items={menuItems}
                        placement={'top-right'}
                        className="approval-actions-menu"
                    />
                )}

                {(!showCompact || (showCompact && menuItems.length < 2)) && (
                    <>
                        {secondaryVotes.map(vote => (
                            <Button
                                key={vote}
                                variant="contained"
                                color={VOTE_CONFIG[vote].color}
                                onPress={() => handleVoteClick(vote)}
                            >
                                {VOTE_CONFIG[vote].label}
                            </Button>
                        ))}

                        {canCancel && (
                            <Button
                                variant="contained"
                                color="attention"
                                onPress={handleCancelClick}
                            >
                                {t('approvals.actions.cancel')}
                            </Button>
                        )}
                    </>
                )}
            </Stack>
        );
    };

    const renderComposingVote = (vote: ApproverVote, text: string) => {

        const config = VOTE_CONFIG[vote];
        const requireRejectionComment = activeStep?.requireRejectionComment ?? (vote === 'REJECTED');
        const effectiveRequired = config.inputRequired || requireRejectionComment;
        const canSubmit = !effectiveRequired || text.trim().length > 0;

        return (
            <Stack spacing={1}>
                <TextField
                    label={config.inputLabel}
                    value={text}
                    onChange={e => setPhase({type: 'composingVote', vote, text: e.target.value})}
                    multiline
                    minRows={2}
                    size="small"
                    required={effectiveRequired}
                    autoFocus
                    fullWidth
                />

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        color={config.color}
                        onPress={handleComposedVoteSubmit}
                        isDisabled={!canSubmit}
                    >
                        {config.label}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onPress={() => setPhase({type: 'idle'})}
                    >
                        Abbrechen
                    </Button>
                </Stack>
            </Stack>
        );
    };

    const renderComposingCancel = (text: string) => (
        <Stack spacing={1}>
            <TextField
                label={CANCEL_INPUT_LABEL}
                value={text}
                onChange={e => setPhase({type: 'composingCancel', text: e.target.value})}
                multiline
                minRows={2}
                size="small"
                autoFocus
                fullWidth
            />

            <Stack direction="row" spacing={1}>
                <Button
                    variant="contained"
                    color="attention"
                    onPress={handleComposedCancelSubmit}
                >
                    Stornieren bestätigen
                </Button>

                <Button
                    variant="outlined"
                    color="secondary"
                    onPress={() => setPhase({type: 'idle'})}
                >
                    Abbrechen
                </Button>
            </Stack>
        </Stack>
    );

    const renderSubmitting = () => (
        <Stack direction="row" spacing={1}>
            <Button variant="contained" color="primary" loading isDisabled>
                Wird verarbeitet…
            </Button>
        </Stack>
    );

    const renderError = (message: string, recoverable: boolean) => (
        <Stack spacing={1}>
            <Alert severity="error" sx={{py: 0.5, fontSize: '0.8rem'}}>
                {message}
            </Alert>
            {recoverable && (
                <Box>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onPress={() => setPhase({type: 'idle'})}
                    >
                        Erneut versuchen
                    </Button>
                </Box>
            )}
        </Stack>
    );

    const content = (() => {
        switch (phase.type) {
            case 'idle':
                return renderIdleButtons();
            case 'composingVote':
                return renderComposingVote(phase.vote, phase.text);
            case 'composingCancel':
                return renderComposingCancel(phase.text);
            case 'submitting':
                return renderSubmitting();
            case 'error':
                return renderError(phase.message, phase.recoverable);
        }
    })();

    return (
        <Box>
            <Collapse in>
                {content}
            </Collapse>
        </Box>
    );
};

export default ApprovalActions;